import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Wand2, Plug, PlugZap, Send, Loader2, AlertTriangle, CheckCircle, XCircle, RefreshCw, Inbox, Trash2,
} from 'lucide-react';
import { cardConsoleApi, cardEngineApi } from '../../services/iso8583Api';
import { PageHeader, Card, CodeBlock, Table, mtiName, cardLink, CardEndpointNote } from './common';

/**
 * Build a message, then put it on a real link.
 *
 * <p>The frames are built by the engine with the switch's own codec, so what a
 * participant is given is a frame the switch demonstrably reads. Sending uses a
 * socket the engine holds on their behalf — which is why the page says, more
 * than once, that nothing here is scored: a certificate is about what their own
 * gateway did.
 */
export default function CardMessageGenerator() {
  // Where the participant's own gateway connects, for the replay command.
  const [health, setHealth] = useState(null);
  useEffect(() => { cardEngineApi.health().then(setHealth).catch(() => setHealth(null)); }, []);
  const endpoint = cardLink(health);
  const [templates, setTemplates] = useState(null);
  const [chosen, setChosen] = useState(null);          // the template id
  const [fields, setFields] = useState([]);            // [{de, name, value, note}]
  const [mti, setMti] = useState('');
  const [generated, setGenerated] = useState(null);
  const [error, setError] = useState(null);

  const [catalogue, setCatalogue] = useState([]);       // every DE the dialect defines
  const [adding, setAdding] = useState('');             // the DE chosen in the picker
  const [link, setLink] = useState({ connected: false });
  const [busy, setBusy] = useState(null);              // 'generate' | 'connect' | 'send'
  const [sent, setSent] = useState(null);
  const [paste, setPaste] = useState('');
  // Off by default: each Generate takes a new trace number and times, so a
  // second message is a new transaction. Tick it to resend the same trace on
  // purpose (a repeat after a lost reply).
  const [keepTrace, setKeepTrace] = useState(false);

  useEffect(() => {
    cardConsoleApi.templates()
      .then((r) => {
        setTemplates(r.templates || []);
        if (r.templates?.length) choose(r.templates[0]);
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
    cardConsoleApi.state().then(setLink).catch(() => setLink({ connected: false }));
  }, []);

  // The catalogue depends on the message type (what it requires) and on what is
  // already in the message, so it is fetched again whenever either moves.
  useEffect(() => {
    if (!mti) return;
    cardConsoleApi.fields(mti, fields.map((f) => f.de))
      .then((r) => setCatalogue(r.fields || []))
      .catch(() => setCatalogue([]));
  }, [mti, fields.map((f) => f.de).join(',')]);

  const addField = (de) => {
    const option = catalogue.find((f) => String(f.de) === String(de));
    if (!option) return;
    setFields([...fields, { de: option.de, name: option.name, value: '', note: option.format }]
      .sort((a, b) => a.de - b.de));
    setAdding('');
    setGenerated(null);
  };

  const removeField = (de) => {
    setFields(fields.filter((f) => f.de !== de));
    setGenerated(null);
  };

  const choose = (template) => {
    setChosen(template.id);
    setMti(template.mti);
    setFields(template.fields.map((f) => ({ ...f })));
    setGenerated(null);
    setError(null);
  };

  // The trace number (DE 11), transmission time (DE 7) and terminal clock
  // (DE 12, 13) were stamped when the page loaded, so a second Generate used to
  // repeat them and the switch answered 94 (duplicate). Fetching the templates
  // again gives values the engine has just stamped from its own sequence; only
  // those elements are replaced, so the participant's other edits stay.
  const TRACE_ELEMENTS = [7, 11, 12, 13];
  const withFreshTrace = async (current) => {
    try {
      const r = await cardConsoleApi.templates();
      const fresh = (r.templates || []).find((t) => t.id === chosen) || (r.templates || [])[0];
      const stamped = Object.fromEntries((fresh?.fields || [])
        .filter((f) => TRACE_ELEMENTS.includes(Number(f.de)))
        .map((f) => [Number(f.de), f.value]));
      return current.map((f) => (stamped[Number(f.de)] != null ? { ...f, value: stamped[Number(f.de)] } : f));
    } catch {
      return current; // the generate call below reports a real failure
    }
  };

  const generate = async () => {
    setBusy('generate');
    setError(null);
    try {
      const current = keepTrace ? fields : await withFreshTrace(fields);
      if (current !== fields) setFields(current);
      const body = {};
      current.forEach((f) => { body[f.de] = f.value; });
      const result = await cardConsoleApi.generate(mti, body);
      setGenerated(result);
      setPaste(result.frameHex);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setGenerated(null);
    } finally {
      setBusy(null);
    }
  };

  const connect = async () => {
    setBusy('connect');
    try {
      setLink(await cardConsoleApi.connect());
    } catch (e) {
      setLink({ connected: false, error: e.response?.data?.error || e.message });
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    setBusy('connect');
    try {
      setLink(await cardConsoleApi.disconnect());
      setSent(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  };

  // A failed send used to do nothing visible, and the page kept saying the link
  // was open after the engine had closed it. Say what failed and re-read the link.
  const send = async () => {
    setBusy('send');
    setError(null);
    try {
      const result = await cardConsoleApi.send(paste);
      setSent(result);
      if (result.state) setLink(result.state);
    } catch (e) {
      setError(`Send failed: ${e.response?.data?.error || e.message}`);
      cardConsoleApi.state().then(setLink).catch(() => setLink({ connected: false }));
    } finally {
      setBusy(null);
    }
  };

  const template = templates?.find((t) => t.id === chosen);

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader icon={Wand2} title="Message Generator">
        Build an ISO 8583 message the switch will read, then send it on a link — or replay the same bytes from your
        own gateway. The frames are assembled by the engine with the switch's own dialect, so nothing here can drift
        from what it accepts.
      </PageHeader>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">
          <strong>This is a rehearsal.</strong> The link below is the Sandbox's own, opened inside it on your behalf,
          so nothing sent from this page is scored — and it does not test your network path either. Send the same
          frame from your gateway for it to count towards
          <Link to="/iso8583/certification" className="underline"> certification</Link>. The
          <Link to="/iso8583/ledger" className="underline"> Message Ledger</Link> records what you rehearse, marked
          <span className="font-mono"> CONSOLE</span>.
        </p>
      </div>

      {error && <p className="text-sm text-red-700">{error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        {/* ── build ─────────────────────────────────────────────────────── */}
        <Card title="1 · Build a message" icon={Wand2}>
          {!templates && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
          {templates && (
            <>
              <label className="block text-xs text-gray-500 mb-1" htmlFor="template">Scenario</label>
              <select id="template" value={chosen || ''} onChange={(e) => choose(templates.find((t) => t.id === e.target.value))}
                className="w-full px-3 py-2 mb-3 rounded-lg border border-gray-300 bg-white text-sm text-gray-900">
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.label} — {t.mti}</option>
                ))}
              </select>
              {template && <p className="text-sm text-gray-700 mb-4">{template.purpose}</p>}

              <div className="flex items-center gap-2 mb-3">
                <label className="text-xs text-gray-500" htmlFor="mti">MTI</label>
                <input id="mti" value={mti} onChange={(e) => setMti(e.target.value)}
                  className="w-24 px-2 py-1 rounded border border-gray-300 font-mono text-sm" />
                <span className="text-xs text-gray-500">{mtiName(mti)}</span>
              </div>

              <div className="space-y-2">
                {fields.map((f, i) => {
                  const option = catalogue.find((c) => c.de === f.de);
                  return (
                    <div key={f.de} className="flex items-start gap-2">
                      <span className="w-12 shrink-0 font-mono text-xs text-gray-500 pt-2">DE {f.de}</span>
                      <div className="flex-1">
                        <input value={f.value || ''}
                          onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                          className="w-full px-2 py-1.5 rounded border border-gray-300 font-mono text-sm text-gray-900" />
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          {f.name}
                          {option?.format && <span className="font-mono"> · {option.format}</span>}
                          {option?.note ? ` — ${option.note}` : (f.note && !option?.format ? ` — ${f.note}` : '')}
                        </p>
                      </div>
                      <button type="button" onClick={() => removeField(f.de)}
                        title={option?.required ? `DE ${f.de} is required in a ${mti}` : `Remove DE ${f.de}`}
                        className="mt-1 p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Every element the dialect defines, so a sample can be made to look
                  like the participant's own traffic — a local time in a sign-on, say. */}
              <div className="mt-3 flex items-center gap-2">
                <select value={adding} onChange={(e) => addField(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700">
                  <option value="">Add a data element…</option>
                  {catalogue.filter((c) => !fields.some((f) => f.de === c.de)).map((c) => (
                    <option key={c.de} value={c.de}>
                      DE {c.de} · {c.name} · {c.format}{c.required ? ' · required' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                Every element this dialect defines is on offer, with the shape a value must take. The codec has the
                last word: one it refuses is reported when the frame is built, naming the element.
              </p>

              <div className="mt-4 flex items-center gap-4 flex-wrap">
                <button type="button" onClick={generate} disabled={busy === 'generate'}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50">
                  {busy === 'generate' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  Generate the frame
                </button>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input type="checkbox" checked={keepTrace} onChange={(e) => setKeepTrace(e.target.checked)}
                    className="rounded border-gray-300" />
                  Keep this trace number and time (to send a repeat)
                </label>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                Each Generate takes a new trace number (DE 11) and transmission time (DE 7), as your gateway would for a
                new transaction.
              </p>
            </>
          )}
        </Card>

        {/* ── the frame ─────────────────────────────────────────────────── */}
        <Card title="2 · The frame" icon={Send}>
          {!generated && <p className="text-sm text-gray-500">Build a message and its bytes appear here.</p>}
          {generated && (
            <>
              <p className="text-xs text-gray-500 mb-1">
                {generated.messageBytes} bytes, behind a 2-byte length prefix — as it goes on the socket:
              </p>
              <CodeBlock>{generated.dump}</CodeBlock>
              <p className="text-xs text-gray-500 mt-3 mb-1">
                As ASCII — the MTI and every field are text on this dialect; the length prefix and the two
                bitmaps are the bytes that are not:
              </p>
              <CodeBlock>{generated.ascii}</CodeBlock>
              <p className="text-xs text-gray-500 mt-3 mb-1">As hex, to paste anywhere:</p>
              <CodeBlock>{generated.frameHex}</CodeBlock>
              <p className="text-xs text-gray-500 mt-3 mb-1">
                As a shell string — every byte exact, ready for <span className="font-mono">printf</span>:
              </p>
              <CodeBlock>{generated.printf}</CodeBlock>
              <Bitmaps bitmaps={generated.bitmaps} />
              {generated.diagnostics?.length > 0 && (
                <div className="mt-3 text-sm text-red-700">
                  {generated.diagnostics.map((d) => <p key={d}>{d}</p>)}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-3 mb-1">Decoded again by the switch's codec:</p>
              <Table head={['DE', 'Value']} mono={[0, 1]}
                rows={Object.entries(generated.fields || {}).map(([de, value]) => [de, value])} />
            </>
          )}
        </Card>
      </div>

      {/* ── the link ───────────────────────────────────────────────────── */}
      <Card title="3 · Send it on a link" icon={link.connected ? PlugZap : Plug}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2 text-sm">
            {link.connected
              ? <CheckCircle className="w-5 h-5 text-green-500" />
              : <XCircle className="w-5 h-5 text-gray-400" />}
            <span className="font-medium text-gray-900">
              {link.connected ? 'Link open' : 'No link'}
            </span>
            {link.connected && (
              <span className="text-gray-500">
                · {link.signedOnAs ? <>signed on as <span className="font-mono">{link.signedOnAs}</span></> : 'not signed on'}
                {' '}· {link.messagesSent} sent · card port <span className="font-mono">{link.cardPort}</span>
              </span>
            )}
            {link.error && <span className="text-red-700">{link.error}</span>}
          </div>
          <div className="flex items-center gap-2">
            {!link.connected ? (
              <button type="button" onClick={connect} disabled={busy === 'connect'}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50">
                {busy === 'connect' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
                Open a link
              </button>
            ) : (
              <>
                <button type="button" onClick={connect} disabled={busy === 'connect'}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">
                  <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
                <button type="button" onClick={disconnect} disabled={busy === 'connect'}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">
                  Close
                </button>
              </>
            )}
          </div>
        </div>

        <p className="text-sm text-gray-700 mb-2">
          A fresh link is <strong>anonymous</strong>, exactly as a gateway's is: send the sign-on first, or a
          transaction is ignored without a reply — which is what the switch does, not a fault.
        </p>

        <label className="block text-xs text-gray-500 mb-1" htmlFor="frame">
          Frame to send — hex, with or without its length prefix. A hex dump pastes too.
        </label>
        <textarea id="frame" value={paste} onChange={(e) => setPaste(e.target.value)} rows={3}
          placeholder="002f30383030…"
          className="w-full px-3 py-2 rounded-lg border border-gray-300 font-mono text-xs text-gray-900" />

        <button type="button" onClick={send} disabled={!link.connected || busy === 'send' || !paste.trim()}
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50">
          {busy === 'send' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send on the link
        </button>

        {sent && <Exchange sent={sent} />}
      </Card>

      <Card title="Replay it from your own gateway" icon={Send}>
        <p className="text-sm text-gray-700 mb-2">
          The same bytes, sent from your own machine — this is the version that counts:
        </p>
        <CardEndpointNote endpoint={endpoint} className="mb-2" />
        <CodeBlock>{endpoint.tls
          ? `# the frame above, straight onto the socket (TLS, by hostname):
{ printf '${generated?.printf || '<generate a message first>'}'; sleep 2; } \\
  | openssl s_client -quiet -no_ign_eof -connect ${endpoint.host}:${endpoint.port} -servername ${endpoint.host} 2>/dev/null \\
  | xxd`
          : `# the frame above, straight onto the socket:
{ printf '${generated?.printf || '<generate a message first>'}'; sleep 2; } | nc ${endpoint.host} ${endpoint.port} | xxd`}</CodeBlock>
        <p className="text-xs text-gray-500 mt-2">
          <Link to="/iso8583/getting-started" className="text-primary-600 hover:underline">Getting Started</Link> has this
          deployment's host and port, the same exchange as a short Python program, and how to check the port is open at all.
        </p>
      </Card>
    </div>
  );
}

/**
 * The bitmaps, spelled out. This is the part of a frame a hex dump does not make
 * legible — 82 20 00 01 is three data elements and an announcement — so the
 * engine says which bits are set and what they mark, and the page shows it.
 */
function Bitmaps({ bitmaps }) {
  if (!bitmaps?.length) return null;
  return (
    <div className="mt-3">
      <p className="text-xs text-gray-500 mb-1">Bitmaps:</p>
      <div className="space-y-2">
        {bitmaps.map((b) => (
          <div key={b.half} className="rounded-lg border border-gray-200 p-2.5">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {b.half === 'PRIMARY' ? 'Primary · DE 1–64' : 'Secondary · DE 65–128'}
              </span>
              <span className="font-mono text-xs text-gray-900">{b.hex}</span>
            </div>
            <p className="font-mono text-[11px] text-gray-600 mt-1 break-all">{b.bits}</p>
            <p className="text-xs text-gray-700 mt-1">
              Marks present:{' '}
              {b.fields?.length
                ? b.fields.map((de) => <span key={de} className="font-mono">DE {de} </span>)
                : <span className="text-gray-500">nothing</span>}
            </p>
            <p className="text-[11px] text-gray-500 mt-0.5">{b.note}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** What happened to one sent frame: the refusal, or the reply and what it decoded to. */
function Exchange({ sent }) {
  if (sent.error) {
    return (
      <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">{sent.error}</div>
    );
  }
  return (
    <div className="mt-4 space-y-4">
      {sent.sent && (
        <div>
          <p className="text-sm font-semibold text-gray-900 mb-1">
            Request · <span className="font-mono">{sent.sent.mti}</span>{' '}
            <span className="text-gray-500 font-normal">{mtiName(sent.sent.mti)}</span>
            <span className="text-gray-400 font-normal"> — as it went on the socket</span>
          </p>
          <CodeBlock>{sent.sent.dump}</CodeBlock>
          <p className="text-xs text-gray-500 mt-2 mb-1">As ASCII:</p>
          <CodeBlock>{sent.sent.ascii}</CodeBlock>
          <Bitmaps bitmaps={sent.sent.bitmaps} />
        </div>
      )}
      {(sent.replies || []).length === 0 && (
        <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700">
          <strong>Nothing came back.</strong> {sent.note}
        </div>
      )}
      {(sent.replies || []).map((reply, i) => (
        <div key={i}>
          <p className="text-sm font-semibold text-gray-900 mb-1">
            Reply · <span className="font-mono">{reply.mti}</span>{' '}
            <span className="text-gray-500 font-normal">{mtiName(reply.mti)}</span>
          </p>
          <CodeBlock>{reply.dump}</CodeBlock>
          <p className="text-xs text-gray-500 mt-2 mb-1">As ASCII:</p>
          <CodeBlock>{reply.ascii}</CodeBlock>
          <Bitmaps bitmaps={reply.bitmaps} />
          {reply.diagnostics?.length > 0 && (
            <div className="mt-2 text-sm text-red-700">
              {reply.diagnostics.map((d) => <p key={d}>{d}</p>)}
            </div>
          )}
          <Table head={['DE', 'Value']} mono={[0, 1]}
            rows={Object.entries(reply.fields || {}).map(([de, value]) => [de, value])} />
        </div>
      ))}
      {(sent.unsolicited || []).length > 0 && (
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
          <p className="font-semibold flex items-center gap-1.5">
            <Inbox className="w-4 h-4" /> The switch also sent, unprompted:
          </p>
          {sent.unsolicited.map((u, i) => (
            <div key={i} className="mt-1">
              <p>
                <span className="font-mono">{u.mti}</span> {mtiName(u.mti)}
                {u.answeredByTheConsole && ' — answered for you, so the link stays open'}
              </p>
              {u.ascii && <p className="font-mono text-xs mt-0.5 break-all">{u.ascii}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
